import React, { useState, useEffect, useRef } from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonInput,
  IonButton,
  IonIcon,
  IonText,
  IonBadge,
} from '@ionic/react';
import { send, chatbubbleOutline, chevronDown, chevronUp } from 'ionicons/icons';
import { GameEvent, ChatMessage } from './types';
import './ChatOverlay.css';

interface ChatOverlayProps {
  events: GameEvent[];
  chatMessages: ChatMessage[];
  currentPlayerId: number;
  currentPlayerName: string;
  onSendMessage: (message: string) => void;
}

const ChatOverlay: React.FC<ChatOverlayProps> = ({
  events,
  chatMessages,
  currentPlayerId,
  currentPlayerName,
  onSendMessage,
}) => {
  const [message, setMessage] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (isExpanded && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [events, chatMessages, isExpanded]);

  const handleSend = () => {
    if (message.trim()) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Combine events and chat messages, sorted by timestamp
  const combinedMessages = [
    ...events.map(e => ({ type: 'event' as const, timestamp: e.timestamp, content: e })),
    ...chatMessages.map(c => ({ type: 'chat' as const, timestamp: c.timestamp, content: c })),
  ].sort((a, b) => a.timestamp - b.timestamp);

  // Get recent messages for collapsed view
  const recentMessages = combinedMessages.slice(-3);

  return (
    <div className={`chat-overlay ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Chevron toggle button - always visible */}
      <div className="chat-toggle">
        <IonButton
          fill="solid"
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          className="toggle-button"
        >
          <IonIcon icon={isExpanded ? chevronDown : chevronUp} slot="icon-only" />
        </IonButton>
        {!isExpanded && combinedMessages.length > 0 && (
          <IonBadge color="primary" className="message-badge">
            {combinedMessages.length}
          </IonBadge>
        )}
      </div>

      {/* Chat content - shown when expanded */}
      {isExpanded && (
        <IonCard className="chat-card">
          <IonCardHeader>
            <IonCardTitle className="chat-header">
              <IonIcon icon={chatbubbleOutline} />
              <span>Chat & Events</span>
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent className="chat-content">
            <div className="messages-container">
              {combinedMessages.map((item, index) => {
                if (item.type === 'event') {
                  const event = item.content as GameEvent;
                  return (
                    <div key={`event-${index}`} className="message event-message">
                      <IonText color="medium" className="event-text">
                        <small>{new Date(event.timestamp).toLocaleTimeString()}</small>
                        {' '}
                        <span>{event.message}</span>
                      </IonText>
                    </div>
                  );
                } else {
                  const chat = item.content as ChatMessage;
                  const isOwnMessage = chat.playerId === currentPlayerId;
                  return (
                    <div
                      key={`chat-${index}`}
                      className={`message chat-message ${isOwnMessage ? 'own-message' : 'other-message'}`}
                    >
                      <div className="message-header">
                        <IonText color={isOwnMessage ? 'primary' : 'secondary'}>
                          <strong>{chat.playerName}</strong>
                        </IonText>
                        <IonText color="medium">
                          <small>{new Date(chat.timestamp).toLocaleTimeString()}</small>
                        </IonText>
                      </div>
                      <div className="message-body">
                        <IonText>{chat.message}</IonText>
                      </div>
                    </div>
                  );
                }
              })}
              <div ref={messagesEndRef} />
            </div>
            <div className="message-input">
              <IonInput
                value={message}
                placeholder="Type a message..."
                onIonInput={(e) => setMessage(e.detail.value || '')}
                onKeyPress={handleKeyPress}
                className="chat-input"
              />
              <IonButton
                onClick={handleSend}
                disabled={!message.trim()}
                size="small"
              >
                <IonIcon icon={send} slot="icon-only" />
              </IonButton>
            </div>
          </IonCardContent>
        </IonCard>
      )}
    </div>
  );
};

export default ChatOverlay;
